import React, { useState } from "react";

type Variant = "primary" | "ink" | "secondary" | "gold" | "ghost" | "inverse";
type Size = "sm" | "md" | "lg";

const SIZES: Record<Size, React.CSSProperties> = {
  sm: { fontSize: "var(--text-caption)", padding: "9px 18px", gap: "6px" },
  md: { fontSize: "var(--text-body-sm)", padding: "13px 26px", gap: "8px" },
  lg: { fontSize: "var(--text-body)", padding: "17px 34px", gap: "10px" },
};

const VARIANTS: Record<Variant, React.CSSProperties> = {
  primary: {
    background: "var(--red-500)",
    color: "var(--white)",
    border: "1px solid var(--red-500)",
  },
  ink: {
    background: "var(--ink-500)",
    color: "var(--cream-100)",
    border: "1px solid var(--ink-500)",
  },
  secondary: {
    background: "transparent",
    color: "var(--ink-500)",
    border: "1px solid var(--ink-500)",
  },
  gold: {
    background: "var(--gold-400)",
    color: "var(--ink-500)",
    border: "1px solid var(--gold-400)",
  },
  ghost: {
    background: "transparent",
    color: "var(--red-500)",
    border: "1px solid transparent",
  },
  inverse: {
    background: "var(--cream-100)",
    color: "var(--ink-500)",
    border: "1px solid var(--cream-100)",
  },
};

const HOVERS: Record<Variant, React.CSSProperties> = {
  primary: { background: "var(--red-900)", borderColor: "var(--red-900)" },
  ink: { background: "var(--ink-900)", borderColor: "var(--ink-900)" },
  secondary: { background: "var(--ink-500)", color: "var(--cream-100)" },
  gold: { background: "var(--gold-700)", borderColor: "var(--gold-700)" },
  ghost: { color: "var(--red-900)", background: "var(--red-50)" },
  inverse: { background: "var(--white)", borderColor: "var(--white)" },
};

interface CpButtonProps {
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  /** Renders a link. External URLs open in a new tab; `#id` scrolls in-page. */
  href?: string;
  onClick?: () => void;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

/** Brand pill button (CertainPath Design System `Button`). */
const CpButton: React.FC<CpButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  href,
  onClick,
  iconLeft,
  iconRight,
  fullWidth = false,
  style,
}) => {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);

  const css: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "var(--font-sans)",
    fontWeight: "var(--weight-bold)" as React.CSSProperties["fontWeight"],
    letterSpacing: "var(--tracking-button)",
    lineHeight: 1.1,
    borderRadius: "var(--radius-pill)",
    cursor: "pointer",
    textDecoration: "none",
    width: fullWidth ? "100%" : undefined,
    transition: "var(--transition-control)",
    transform: press
      ? "var(--press-scale)"
      : hover
        ? "var(--lift-hover)"
        : "none",
    boxShadow:
      hover && (variant === "primary" || variant === "ink")
        ? "var(--shadow-accent)"
        : "none",
    ...SIZES[size],
    ...VARIANTS[variant],
    ...(hover ? HOVERS[variant] : null),
    ...style,
  };

  const handlers = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => {
      setHover(false);
      setPress(false);
    },
    onMouseDown: () => setPress(true),
    onMouseUp: () => setPress(false),
  };

  const content = (
    <>
      {iconLeft}
      <span>{children}</span>
      {iconRight}
    </>
  );

  if (href) {
    const external = /^(https?:|mailto:)/.test(href);
    return (
      <a
        href={href}
        style={css}
        {...handlers}
        {...(external && !href.startsWith("mailto:")
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {content}
      </a>
    );
  }

  return (
    <button onClick={onClick} style={css} type="button" {...handlers}>
      {content}
    </button>
  );
};

export default CpButton;
